import React from 'react'
import Button from '@cloudscape-design/components/button'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'

/**
 * ErrorBoundary component
 * Catches JavaScript errors anywhere in the child component tree
 * Logs errors and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    })
  }

  handleReset = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null
    })
    
    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          type="error"
          header="Something went wrong"
          action={<Button onClick={this.handleReset}>Try Again</Button>}
        >
          <Box variant="p">
            {this.props.fallbackMessage || 
              'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
          </Box>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box variant="code" padding={{ top: 's' }}>
              <strong>Error Details:</strong>
              <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                {this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </Box>
          )}
        </Alert>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
